import type { AdBreak, AdCreative } from '../types'

function text(el: Element | null): string {
  return (el?.textContent || '').trim()
}

function attrs(el: Element, name: string): string {
  return el.getAttribute(name) || ''
}

function collectTracking(creative: Element, eventName: string): string[] {
  const urls: string[] = []
  creative.querySelectorAll('Tracking').forEach((t) => {
    if ((attrs(t, 'event') || '').toLowerCase() === eventName.toLowerCase()) {
      const u = text(t)
      if (u) urls.push(u)
    }
  })
  return urls
}

function parseSkipOffset(raw: string, duration: number): number {
  if (!raw) return Math.min(5, Math.floor(duration / 2) || 5)
  if (raw.endsWith('%')) {
    const pct = Number(raw.slice(0, -1))
    return Number.isFinite(pct) ? (duration * pct) / 100 : 5
  }
  const parts = raw.split(':').map(Number)
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  const n = Number(raw)
  return Number.isFinite(n) ? n : 5
}

function parseDuration(raw: string): number {
  const parts = raw.split(':').map(Number)
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function pickMediaFile(ad: Element): string | null {
  const files = Array.from(ad.querySelectorAll('MediaFile'))
  const mp4 = files.find((f) => /mp4/i.test(attrs(f, 'type') || text(f)))
  const any = mp4 || files[0]
  return any ? text(any) : null
}

export function parseVastXml(xml: string, breakId = 'vast-pre'): AdBreak | null {
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  if (doc.querySelector('parsererror')) return null
  const adEl = doc.querySelector('Ad')
  if (!adEl) return null

  const impressions = Array.from(adEl.querySelectorAll('Impression'))
    .map((i) => text(i))
    .filter(Boolean)

  const linear = adEl.querySelector('Linear')
  if (!linear) return null

  const duration = parseDuration(text(linear.querySelector('Duration')))
  const skipAfter = parseSkipOffset(attrs(linear, 'skipoffset'), duration)
  const src = pickMediaFile(adEl)
  if (!src) return null

  const clickThrough = text(adEl.querySelector('ClickThrough'))
  const title = text(adEl.querySelector('AdTitle')) || 'Advertisement'

  const creative: AdCreative = {
    id: attrs(adEl, 'id') || `${breakId}-0`,
    src,
    skipAfterSeconds: skipAfter,
    clickThroughUrl: clickThrough || undefined,
    title,
    tracking: {
      impression: impressions,
      start: collectTracking(linear, 'start'),
      firstQuartile: collectTracking(linear, 'firstQuartile'),
      midpoint: collectTracking(linear, 'midpoint'),
      thirdQuartile: collectTracking(linear, 'thirdQuartile'),
      complete: collectTracking(linear, 'complete'),
      skip: collectTracking(linear, 'skip'),
    },
  }

  return {
    id: breakId,
    type: 'preroll',
    ads: [creative],
  }
}

export async function fetchVast(url: string): Promise<AdBreak | null> {
  const res = await fetch(url, { credentials: 'omit' })
  if (!res.ok) throw new Error(`VAST HTTP ${res.status}`)
  const xml = await res.text()
  // Follow one Wrapper level
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  const vastAdTag = text(doc.querySelector('VASTAdTagURI'))
  if (vastAdTag) {
    const nested = await fetch(vastAdTag, { credentials: 'omit' })
    if (!nested.ok) throw new Error(`VAST wrapper HTTP ${nested.status}`)
    return parseVastXml(await nested.text())
  }
  return parseVastXml(xml)
}

export function fireTrackingUrls(urls: string[] | undefined) {
  if (!urls?.length) return
  for (const u of urls) {
    try {
      const img = new Image()
      img.src = u.replace(/\[CACHEBUSTING\]/gi, String(Date.now()))
    } catch {
      /* ignore */
    }
  }
}
